// src/pages/ResetPassword.tsx
import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Area, Button } from '../styles/_app.style';
import {
  RegisterArea,
  FormArea,
  Form,
  Tagline,
  FormActions,
  Field,
  Label,
  Control,
  Message,
} from '../styles/login.style';
import { BookLogo } from '../components/common/BookLogo';
import { AuthSkyline } from '../components/common/AuthSkyline';
import { NAVIGATION_HEIGHT } from '../constants/global';
import { useScale } from './HeightScaler';
import { baseURL } from '../constants/endpoints';
import axios from 'axios';
import { useToast } from '../contextManager/toast.context';

type Status = 'idle' | 'submitting' | 'success' | 'error' | 'expired';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { scale } = useScale();
  const { addToast, updateToast } = useToast();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setStatus('error');
      setErrorMsg('Invalid reset link.');
      return;
    }

    if (password.length < 6) {
      setErrorMsg('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    const toastId = `reset-password-${Date.now()}`;
    addToast({ id: toastId, variant: 'loading', title: 'Resetting password...' });

    try {
      await axios.post(`${baseURL}/password/reset`, { token, password });
      setStatus('success');
      updateToast(toastId, { variant: 'success', title: 'Password reset!' });
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Something went wrong.';
      if (msg.includes('expired')) {
        setStatus('expired');
      } else {
        setStatus('error');
      }
      setErrorMsg(msg);
      updateToast(toastId, { variant: 'error', title: msg });
    }
  };

  const messages: Record<string, { title: string; body: string }> = {
    success: {
      title: 'Password reset!',
      body: 'Your password has been updated. You can now log in with your new password.',
    },
    expired: {
      title: 'Link expired',
      body: 'This reset link has expired. Please request a new one.',
    },
    error: {
      title: 'Reset failed',
      body: errorMsg || 'This link is invalid. Please request a new reset email.',
    },
  };

  if (status === 'success' || status === 'expired') {
    const { title, body } = messages[status];
    return (
      <>
        <Helmet><title>Reset Password - tute</title></Helmet>
        <RegisterArea>
          <AuthSkyline />
          <Area css={{
            height: `calc((100dvh / ${scale}) - ${NAVIGATION_HEIGHT})`,
            marginTop: `calc(${NAVIGATION_HEIGHT})`,
            display: 'flex', flexDirection: 'column',
          }}>
            <FormArea>
              <Form onSubmit={(e) => e.preventDefault()}>
                <div style={{ display: 'flex', flexDirection: 'column', width: 'max-content', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '1.8rem', lineHeight: '1', color: 'var(--colors-slate12, #1e293b)', letterSpacing: '-0.5px' }}>tute</span>
                  <div style={{ width: '100%', height: '16px', marginTop: '0.1rem' }}><BookLogo /></div>
                </div>
                <Tagline style={{ marginBottom: '0.5rem', fontWeight: 600 }}>{title}</Tagline>
                <p style={{ color: '#666', textAlign: 'center', margin: '1rem 0' }}>{body}</p>
                <FormActions>
                  <Button type="button" block tone="success" onClick={() => navigate(status === 'expired' ? '/forgot-password' : '/login')}>
                    {status === 'expired' ? 'Request New Link' : 'Go to Login'}
                  </Button>
                </FormActions>
              </Form>
            </FormArea>
          </Area>
        </RegisterArea>
      </>
    );
  }

  return (
    <>
      <Helmet><title>Reset Password - tute</title></Helmet>
      <RegisterArea>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <img src="/assets/kings-chapel.jpg" alt="" style={{ maxHeight: '70%', objectFit: 'contain', opacity: 0.06, filter: 'grayscale(100%)' }} />
        </div>
        <Area css={{
          height: `calc((100dvh / ${scale}) - ${NAVIGATION_HEIGHT})`,
          marginTop: `calc(${NAVIGATION_HEIGHT})`,
          display: 'flex', flexDirection: 'column',
        }}>
          <FormArea>
            <Form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', width: 'max-content', alignItems: 'center' }}>
                <span style={{ fontWeight: 'bold', fontSize: '1.8rem', lineHeight: '1', color: 'var(--colors-slate12, #1e293b)', letterSpacing: '-0.5px' }}>tute</span>
                <div style={{ width: '100%', height: '16px', marginTop: '0.1rem' }}><BookLogo /></div>
              </div>

              <Tagline style={{ marginBottom: '0.5rem' }}>Choose a new password</Tagline>

              {!token && (
                <p style={{ color: '#dc2626', fontSize: '0.875rem', textAlign: 'center' }}>
                  Invalid reset link. Please request a new one.
                </p>
              )}

              <Field name="password" css={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <Label>New Password</Label>
                <Control
                  type="password"
                  required
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPassword(e.target.value); setErrorMsg(''); }}
                />
                <Message match="valueMissing">Please enter a password</Message>
              </Field>

              <Field name="confirmPassword" css={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <Label>Confirm Password</Label>
                <Control
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setConfirmPassword(e.target.value); setErrorMsg(''); }}
                />
                <Message match="valueMissing">Please confirm your password</Message>
              </Field>

              {errorMsg && (
                <p style={{ color: '#dc2626', fontSize: '0.875rem', textAlign: 'center' }}>{errorMsg}</p>
              )}

              <FormActions>
                <Button type="submit" block tone="success" disabled={status === 'submitting' || !token}>
                  {status === 'submitting' ? 'Resetting…' : 'Reset Password'}
                </Button>
                <a href="/forgot-password" style={{ marginTop: '1rem', display: 'inline-block' }}>
                  Request a new link
                </a>
              </FormActions>
            </Form>
          </FormArea>
        </Area>
      </RegisterArea>
    </>
  );
};

export default ResetPassword;